import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight, Phone, Mail, MapPin } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { WhatsAppFloat } from "@/components/WhatsAppFloat";

interface LandingPageData {
  id: string;
  title: string;
  subtitle: string | null;
  hero_content: any; // Use any for JSON fields from Supabase
  features: any[]; // Use any for JSON fields from Supabase 
  pricing_section: boolean;
  contact_form: boolean;
  systems: {
    id: string;
    code: string;
    name: string;
    description: string | null;
    color: string | null;
  };
}

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  company: string;
  message: string;
}

export default function DynamicLanding() {
  const { systemCode } = useParams<{ systemCode: string }>();
  const [contactData, setContactData] = useState<ContactForm>({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: landingData, isLoading } = useQuery({
    queryKey: ['landing-page', systemCode],
    queryFn: async (): Promise<LandingPageData | null> => {
      if (!systemCode) return null;

      const { data, error } = await supabase
        .from('landing_pages')
        .select(`
          *,
          systems!inner(
            id,
            code,
            name,
            description,
            color
          )
        `)
        .eq('systems.code', systemCode)
        .eq('is_active', true)
        .single();

      if (error) throw error;
      return data as LandingPageData;
    },
    enabled: !!systemCode,
  });

  const { data: systemPlans } = useQuery({
    queryKey: ['system-plans', landingData?.systems?.id],
    queryFn: async () => {
      if (!landingData?.systems?.id) return [];

      const { data, error } = await supabase
        .from('plans')
        .select('*')
        .eq('system_id', landingData.systems.id)
        .eq('is_active', true)
        .order('price_monthly', { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!landingData?.systems?.id,
  });

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Contact form submission - just show success message
      // Lead tracking system discontinued
      console.log('Landing page contact submitted:', {
        ...contactData,
        system_code: systemCode,
      });

      toast.success("Mensagem enviada com sucesso! Entraremos em contato em breve.");
      setContactData({
        name: "",
        email: "",
        phone: "",
        company: "",
        message: ""
      });
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      toast.error("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!landingData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Sistema não encontrado</h1>
          <p className="text-muted-foreground">O sistema "{systemCode}" não foi encontrado ou não possui landing page configurada.</p>
        </div>
      </div>
    );
  }

  const systemColor = landingData.systems.color || '#3b82f6';

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="py-20 px-4 text-center relative"
        style={{
          background: `linear-gradient(135deg, ${systemColor}15 0%, ${systemColor}05 100%)`
        }}
      >
        <div className="max-w-6xl mx-auto">
          <Badge variant="outline" className="mb-6">
            {landingData.systems.name}
          </Badge>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {landingData.title}
          </h1>
          
          {landingData.subtitle && (
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              {landingData.subtitle}
            </p>
          )}
          
          {landingData.hero_content?.description && (
            <p className="text-lg mb-10 max-w-4xl mx-auto">
              {landingData.hero_content.description}
            </p>
          )}
          
          <Button size="lg" className="mr-4">
            {landingData.hero_content?.cta_text || "Começar agora"}
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="lg">
            Saber mais
          </Button>

          {landingData.hero_content?.benefits && Array.isArray(landingData.hero_content.benefits) && (
            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {landingData.hero_content.benefits.map((benefit: string, index: number) => (
                <div key={index} className="flex items-center justify-center">
                  <Check className="h-5 w-5 text-green-500 mr-2" />
                  <span className="text-sm">{benefit}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Features Section */}
      {landingData.features && Array.isArray(landingData.features) && landingData.features.length > 0 && (
        <section className="py-20 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Recursos Principais
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {landingData.features.map((feature: any, index: number) => (
                <Card key={index} className="text-center">
                  <CardHeader>
                    <div 
                      className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${systemColor}20` }}
                    >
                      <div className="w-8 h-8" style={{ color: systemColor }}>
                        {/* Icon placeholder - could be enhanced with actual icons */}
                        ⚡
                      </div>
                    </div>
                    <CardTitle className="text-xl">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{feature.description}</CardDescription>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing Section */}
      {landingData.pricing_section && systemPlans && systemPlans.length > 0 && (
        <section className="py-20 px-4 bg-muted/30">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Planos e Preços
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {systemPlans.map((plan) => (
                <Card key={plan.id} className="relative">
                  <CardHeader>
                    <CardTitle className="text-2xl">{plan.name}</CardTitle>
                    <CardDescription>{plan.description}</CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold">
                        {plan.price_monthly === 0 ? 'Grátis' : `R$ ${(plan.price_monthly / 100).toFixed(0)}`}
                      </span>
                      {plan.price_monthly > 0 && (
                        <span className="text-muted-foreground">/mês</span>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <Button className="w-full" variant={plan.name === 'Pro' ? 'default' : 'outline'}>
                      Escolher Plano
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Contact Section */}
      {landingData.contact_form && (
        <section className="py-20 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12">
              Entre em Contato
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-semibold mb-6">Fale Conosco</h3>
                <p className="text-muted-foreground mb-8">
                  Tem dúvidas sobre o {landingData.title}? Nossa equipe está pronta para ajudar você a encontrar a melhor solução.
                </p>
                
                <div className="space-y-4">
                  <div className="flex items-center">
                    <Phone className="h-5 w-5 mr-3 text-primary" />
                    <span>(11) 9999-9999</span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-5 w-5 mr-3 text-primary" />
                    <span>contato@exemplo.com</span>
                  </div>
                  <div className="flex items-center">
                    <MapPin className="h-5 w-5 mr-3 text-primary" />
                    <span>São Paulo, SP</span>
                  </div>
                </div>
              </div>
              
              <form onSubmit={handleContactSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Nome</label>
                    <input
                      type="text"
                      required
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={contactData.name}
                      onChange={(e) => setContactData({...contactData, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      required
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={contactData.email}
                      onChange={(e) => setContactData({...contactData, email: e.target.value})}
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Telefone</label>
                    <input
                      type="tel"
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={contactData.phone}
                      onChange={(e) => setContactData({...contactData, phone: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Empresa</label>
                    <input
                      type="text"
                      className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      value={contactData.company}
                      onChange={(e) => setContactData({...contactData, company: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Mensagem</label>
                  <textarea
                    rows={4}
                    required
                    className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    value={contactData.message}
                    onChange={(e) => setContactData({...contactData, message: e.target.value})}
                  />
                </div>
                
                <Button type="submit" className="w-full" disabled={isSubmitting}>
                  {isSubmitting ? "Enviando..." : "Enviar Mensagem"}
                </Button>
              </form>
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 px-4 border-t">
        <div className="max-w-6xl mx-auto text-center">
          <p className="text-muted-foreground">
            © 2024 {landingData.title}. Todos os direitos reservados.
          </p>
        </div>
      </footer>
      
      {/* WhatsApp Float */}
      <WhatsAppFloat systemName={landingData.systems.name} />
    </div>
  );
}